import { useEffect, useState } from 'react';
import { currentEntryScriptPath, fetchLatestEntryScriptPath } from '~/utils/staleBuild';

const DEFAULT_INTERVAL_MS = 5 * 60 * 1000;
const DEFAULT_MIN_GAP_MS = 30 * 1000;
const DEFAULT_TIMEOUT_MS = 15 * 1000;

export interface UseStaleBuildOptions {
  /**
   * On by default: the dev server and tests have no hashed entry script, so it is inert there.
   * It doesn't key on `PROD`, which deployed builds didn't always set (see MCV-1957).
   */
  enabled?: boolean;
  intervalMs?: number;
  /** No two checks start closer together than this. */
  minGapMs?: number;
  /** A check still pending after this long is aborted, so a stalled request can't block later ones. */
  timeoutMs?: number;
}

/**
 * [MCV-1948] True once the deployed index.html names a different entry script than the one
 * this tab loaded, i.e. the tab is running an old build. Checks when the tab becomes visible
 * or focused and on an interval (visible tabs only). A failed or non-HTML fetch says nothing
 * (staging shuts down nightly), so it is ignored. During a rolling deploy old and new tasks
 * both serve `/`, so one mismatch could be an old task: only the same new entry on two checks
 * in a row counts, and the first mismatch schedules the confirming check ~31s later rather
 * than waiting for the interval. Stops checking once stale.
 */
export const useStaleBuild = ({
  enabled = true,
  intervalMs = DEFAULT_INTERVAL_MS,
  minGapMs = DEFAULT_MIN_GAP_MS,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: UseStaleBuildOptions = {}): boolean => {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const loadedWith = currentEntryScriptPath();
    if (!loadedWith) return;

    let inFlight: AbortController | null = null;
    let lastStartedAt = Number.NEGATIVE_INFINITY;
    let stopped = false;
    let candidate: string | null = null;
    let confirmTimer: number | undefined;

    const check = async () => {
      if (stopped || inFlight || Date.now() - lastStartedAt < minGapMs) return;
      lastStartedAt = Date.now();
      const controller = new AbortController();
      inFlight = controller;
      // A stalled request or body would otherwise hold `inFlight` and skip every later check.
      const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
      const latest = await fetchLatestEntryScriptPath(controller.signal);
      window.clearTimeout(timeout);
      if (inFlight === controller) inFlight = null;
      if (stopped || controller.signal.aborted) return;
      // A failed check schedules nothing; the next focus, visibility change or tick tries again.
      if (latest === null) return;
      if (latest === loadedWith) {
        candidate = null;
        window.clearTimeout(confirmTimer);
        return;
      }
      if (latest !== candidate) {
        candidate = latest;
        // Confirm soon rather than on the next interval tick: a tab coming back after a deploy is
        // exactly when the user is about to act on the old build.
        window.clearTimeout(confirmTimer);
        // A hidden tab is confirmed by the visibility listener when it comes back.
        confirmTimer = window.setTimeout(() => {
          if (document.visibilityState === 'visible') void check();
        }, minGapMs + 1000);
        return;
      }
      stop();
      setStale(true);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void check();
    };
    const onFocus = () => void check();
    // A hidden tab is checked again by the visibility listener when it comes back.
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void check();
    }, intervalMs);

    function stop() {
      stopped = true;
      window.clearInterval(timer);
      window.clearTimeout(confirmTimer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
      inFlight?.abort();
    }

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    return stop;
  }, [enabled, intervalMs, minGapMs, timeoutMs]);

  return stale;
};
